import React, { useRef, useState, useEffect } from 'react';
import { useI18n } from '../contexts/I18nContext';
import { ensureAnnotateX } from '../utils/annotatex';
import './ImageViewer.css';

// The interactive overlay (annotation shapes, tooltip, click-to-open dialog,
// lightbox, legend and prev/next controls) is rendered by the annotateX-js
// embed over the backend-generated SVG. This component only supplies the
// surrounding chrome (header, description) and keeps the embed's theme in sync.

export default function ImageViewer({ image, onClose, onEdit }) {
  const { t } = useI18n();
  const hostRef = useRef(null);
  const [isMax, setIsMax] = useState(false);
  // The annotation currently hovered in the embed, surfaced from the
  // annotateX:tooltipOpen / tooltipClose custom DOM events.
  const [active, setActive] = useState(null);
  // Cache-bust the SVG so reopening reflects the latest annotations.
  const [ts] = useState(() => Date.now());

  // Keep the embed's light/dark theme in sync with the app's data-theme.
  useEffect(() => {
    let observer;
    ensureAnnotateX().then((ax) => {
      const apply = () =>
        ax.setTheme(document.documentElement.getAttribute('data-theme') === 'light' ? 'light' : 'dark');
      apply();
      observer = new MutationObserver(apply);
      observer.observe(document.documentElement, { attributes: true, attributeFilter: ['data-theme'] });
    });
    return () => observer && observer.disconnect();
  }, []);

  // Mount the annotateX `<object>` embed inside a host div React leaves empty,
  // so the library can wrap/augment the DOM without fighting React's reconciler.
  useEffect(() => {
    let obj;
    let inst = null; // the resolved AnnotateX instance (build() result)
    let cancelled = false;
    const onTip = (e) => setActive(e.annotation || null);
    const offTip = () => setActive(null);
    ensureAnnotateX().then((ax) => {
      if (cancelled || !hostRef.current) return;
      ax.texts.header = ''; // the app provides its own header bar

      obj = document.createElement('object');
      obj.type = 'image/svg+xml';
      obj.data = `/public/images/${image.id}.svg?_=${ts}`;
      obj.className = 'viewer-embed-object';
      // Preserve the image's aspect ratio when there is no overlay to size it
      // (images without annotations skip the autoResize fit logic). Harmless
      // once annotateX wraps the object and pins width/height to 100%.
      if (image.original_width && image.original_height) {
        obj.style.aspectRatio = `${image.original_width} / ${image.original_height}`;
      }
      hostRef.current.appendChild(obj);
      // Reflect the hovered annotation in our header via the custom events.
      obj.addEventListener('annotateX:tooltipOpen', onTip);
      obj.addEventListener('annotateX:tooltipClose', offTip);
      // Fit the image so the legend stays visible (no scroll), drop the
      // legend's title/hint (items open the dialog on click) and the navigator.
      // build() resolves with the instance once the full UI is in place.
      ax.build(obj, { autoResize: true, legendTitle: false, controls: false })
        .then((built) => { if (cancelled) built.destroy(); else inst = built; })
        .catch(() => {});
    });
    return () => {
      cancelled = true;
      if (obj) {
        obj.removeEventListener('annotateX:tooltipOpen', onTip);
        obj.removeEventListener('annotateX:tooltipClose', offTip);
      }
      if (inst) inst.destroy();
      else if (obj && window.annotateX) window.annotateX.destroy(obj);
      if (hostRef.current) hostRef.current.innerHTML = '';
    };
  }, [image.id, ts]);

  const imgTags = image.tags || [];

  return (
    <div className={`viewer-overlay${isMax ? ' maximized' : ''}`} onClick={onClose}>
      <div className={`viewer-container${isMax ? ' maximized' : ''}`} onClick={e => e.stopPropagation()}>
        <div className="viewer-header">
          <div>
            <h2 className="viewer-title">{image.name}</h2>
            <span className="badge badge-accent">{image.annotations.length} {t('image.annotations_count')}</span>
            <span style={{ marginLeft:8 }} className="badge badge-success">{image.original_width}×{image.original_height}</span>
            {imgTags.map(t => (
              <span key={t.id} className="tag-pill tag-pill-sm" style={{ '--tag-color': t.color, marginLeft: 6 }}>{t.name}</span>
            ))}
            {active && (active.title || active.label) && (
              <span className="badge badge-accent" style={{ marginLeft: 8 }}>◉ {active.title || active.label}</span>
            )}
          </div>
          <div style={{ display:'flex', gap:8 }}>
            <button className="btn btn-secondary btn-sm" title={isMax ? t('common.restore') : t('common.maximize')} onClick={() => setIsMax(v => !v)}>{isMax ? '⤡' : '⤢'}</button>
            <button className="btn btn-secondary btn-sm" onClick={onEdit}>{t('image.edit_annotations')}</button>
            <button className="btn btn-secondary btn-sm" onClick={onClose}>{t('image.close_btn')}</button>
          </div>
        </div>

        <div className="viewer-canvas-wrap">
          <div className="viewer-embed-host" ref={hostRef} />
        </div>

        {image.description && (
          <div className="viewer-description" dangerouslySetInnerHTML={{ __html: image.description }} />
        )}
      </div>
    </div>
  );
}
